import {useLocalObservable} from 'mobx-react-lite';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ECancelSubmitButtonRow, EForm} from 'webapp-lib/Components';
import {useMountEffect} from 'webapp-lib/utils';

import {Dialog} from '#';
import {InfoText} from '#/InfoText';
import {useDialogController} from '_/hooks';
import {useTablePersistence} from '_/hooks/useTablePersistence';
import {EFRadioGroup, useForm} from '_/utilities/form';
import {TranslatedActivatableItem} from '_/utilities/masterdata.utils';
import {assertUnreachable} from '_/utilities/types';

import {OptionFilterInfo} from '../OptionFilterInfo/OptionFilterInfo';

import {ActiveFilters} from './ActiveFilter.store';
import {ActiveFilterColumn, ActiveFilterRow} from './ActiveFilter.styles';
import {activeStateItems} from './ActiveFilter.utils';

export type UseActiveFiltersProps = {
  options: TranslatedActivatableItem[];
  persistenceKey: string;
  onFilterChange?: (options: TranslatedActivatableItem[]) => void;
  showFilteredOutInfo?: boolean;
};

export const useActiveFilters = ({
  options,
  persistenceKey,
  onFilterChange,
  showFilteredOutInfo = false,
}: UseActiveFiltersProps): {
  activeOptions: TranslatedActivatableItem[];
  persistenceInitialized: boolean;
  filtersModified: boolean;
  activeFilterText: string;
  showActiveFiltersDialog: () => void;
  activeFiltersDialog: React.ReactElement | undefined;
  optionFilterInfo: React.ReactElement | undefined;
} => {
  const {isVisible, showDialog, closeDialog} = useDialogController();
  const activeFilters = useLocalObservable(() => new ActiveFilters());
  const [activeOptions, setActiveOptions] = useState(options);

  const persistenceInitialized = useTablePersistence({
    key: `active-filter.${persistenceKey}`,
    filters: activeFilters.filters,
  });

  const applyFilters = () => {
    let newOptions: TranslatedActivatableItem[];
    switch (activeFilters.activeFilter.item) {
      case 'ALL':
        newOptions = options;
        break;
      case 'ACTIVE':
        newOptions = options.filter(({active}) => active);
        break;
      case 'INACTIVE':
        newOptions = options.filter(({active}) => !active);
        break;
      default:
        assertUnreachable(activeFilters.activeFilter.item);
    }
    onFilterChange?.(newOptions);
    setActiveOptions(newOptions);
  };

  useMountEffect(applyFilters);

  const activeFiltersDialog = isVisible ? (
    <ActiveFiltersDialog
      filters={activeFilters}
      showFilteredOutInfo={showFilteredOutInfo}
      onClose={(updated) => {
        if (updated) {
          applyFilters();
        }
        closeDialog();
      }}
    />
  ) : undefined;

  const optionFilterInfo = (
    <OptionFilterInfo
      optionFilterText={activeFilters.activeFilterText}
      showFilteredOutWarning={false}
    />
  );

  return {
    activeOptions,
    persistenceInitialized,
    filtersModified: activeFilters.modified,
    activeFilterText: activeFilters.activeFilterText,
    showActiveFiltersDialog: showDialog,
    activeFiltersDialog,
    optionFilterInfo,
  };
};

enum ActiveFiltersDialogFormField {
  ActiveState = 'activeState',
}

export const ActiveFiltersDialog = ({
  filters,
  onClose,
  showFilteredOutInfo,
}: {
  filters: ActiveFilters;
  onClose: (updated?: boolean) => void;
  showFilteredOutInfo: boolean;
}): React.ReactElement => {
  const {t} = useTranslation('common');

  const {control, handleSubmit, formState} = useForm({
    defaultValues: {
      [ActiveFiltersDialogFormField.ActiveState]: filters.activeFilter.item,
    },
    onSubmit: ({activeState}) => {
      const updated = filters.activeFilter.item !== activeState;
      filters.activeFilter.setElements(activeState);
      onClose(updated);
    },
  });

  const dialogContent = (
    <EForm onSubmit={handleSubmit}>
      <ActiveFilterRow>
        <ActiveFilterColumn>
          <EFRadioGroup
            control={control}
            items={activeStateItems}
            label={t('activeFilter.dialog.activeStateFilter')}
            name={ActiveFiltersDialogFormField.ActiveState}
          />
        </ActiveFilterColumn>
      </ActiveFilterRow>
      {showFilteredOutInfo ? (
        <InfoText text={t('activeFilter.dialog.selectedFilteredOutInfo')} />
      ) : null}
      <ECancelSubmitButtonRow
        isSubmitting={formState.isSubmitting}
        submitButtonText={t('actions.apply')}
        onCancel={onClose}
      />
    </EForm>
  );

  return (
    <Dialog
      headerText={t('activeFilter.dialog.header')}
      size='tiny'
      isShown
      onClose={onClose}
    >
      {dialogContent}
    </Dialog>
  );
};
